import { z } from 'zod';
import {
  BOOK_DESCRIPTION_MAX_LENGTH,
  BOOK_MAX_LENGTH,
  DEFAULT_BOOK,
  LIMIT_MARGIN_MAX_BPS,
} from '../../config.ts';
import type { InstrumentKind } from '../instrument.ts';
import { idSchema } from '../primitives.ts';

const BOOK_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

export function normaliseBook(raw: string): string | null {
  const book = raw.trim().toLowerCase();
  return book.length <= BOOK_MAX_LENGTH && BOOK_PATTERN.test(book) ? book : null;
}

export const BOOK_RULE = `book must be 1–${BOOK_MAX_LENGTH} letters, digits or hyphens, e.g. zee-business`;

export const bookSchema = z
  .string()
  .transform(normaliseBook)
  .refine((book): book is string => book !== null, { message: BOOK_RULE });

export const bookIdSchema = idSchema;

export const noSuchBook = (bookId: number): string => `book #${bookId} not found`;

export const bookDescriptionSchema = z.string().trim().min(1).max(BOOK_DESCRIPTION_MAX_LENGTH);

export const isNewBook = (book: string, known: readonly string[]): boolean =>
  book !== DEFAULT_BOOK && !known.includes(book);

export interface BookAt {
  analystId: number;
  bookId: number;
}

export interface BookNamedAt extends BookAt {
  book: string;
}

export interface LimitMargins {
  limitMarginBps: number | null;
  optionLimitMarginBps: number | null;
}

export const DESK_LIMIT_MARGINS = { outright: 15, option: 50 } as const;

export const NO_LIMIT_MARGINS: LimitMargins = { limitMarginBps: null, optionLimitMarginBps: null };

export const limitMarginSchemas = {
  limitMarginBps: z.number().int().min(0).max(LIMIT_MARGIN_MAX_BPS.outright),
  optionLimitMarginBps: z.number().int().min(0).max(LIMIT_MARGIN_MAX_BPS.option),
};

export const limitMarginBps = (book: LimitMargins, kind: InstrumentKind): number =>
  kind === 'OPTION'
    ? (book.optionLimitMarginBps ?? DESK_LIMIT_MARGINS.option)
    : (book.limitMarginBps ?? DESK_LIMIT_MARGINS.outright);

/** An option or a commodity always, anything else where it names no book. */
export const TELEGRAM_KINDS = ['options', 'commodities', 'unnamed'] as const;
export type TelegramKind = (typeof TELEGRAM_KINDS)[number];

/** Opened on first use. */
export const RULE_BOOKS: Record<TelegramKind, string> = {
  options: 'scalp',
  commodities: 'commodity',
  unnamed: 'equity',
};

export const TAKES_COLUMN = {
  options: 'takesOptions',
  commodities: 'takesCommodities',
  unnamed: 'takesUnnamed',
} as const satisfies Record<TelegramKind, string>;

export type Takes = { [K in (typeof TAKES_COLUMN)[TelegramKind]]: boolean };

export const takenKinds = (book: Takes): TelegramKind[] =>
  TELEGRAM_KINDS.filter((kind) => book[TAKES_COLUMN[kind]]);

export interface BookSettings extends LimitMargins, Takes {
  description: string | null;
  archived: boolean;
}
